import React, { useState } from 'react';

/**
 * Field Validator Component - Checkmark/X buttons for validating individual fields
 * @param {string} fieldName - Unique field identifier
 * @param {boolean|null} currentStatus - true=correct, false=wrong, null=not validated
 * @param {function} onValidate - Callback when validated (fieldName, isCorrect)
 * @param {object} labels - Custom labels {true: 'Correct', false: 'Wrong'}
 * @param {string} size - sm, md, lg (default: sm)
 * @param {boolean} disabled - Disable interactions (default: false)
 * @param {string} className - Additional CSS classes
 */
const FieldValidator = ({
  fieldName,
  currentStatus = null,
  onValidate = null,
  labels = { true: 'Correct', false: 'Wrong' },
  size = 'sm',
  disabled = false,
  className = ''
}) => {
  const [localStatus, setLocalStatus] = useState(currentStatus);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const sizeClasses = {
    sm: 'btn-sm',
    md: '',
    lg: 'btn-lg'
  };

  const handleValidation = async (isCorrect) => {
    if (disabled || isSubmitting) return;

    // Toggle off if clicking same button
    const newStatus = localStatus === isCorrect ? null : isCorrect;
    setLocalStatus(newStatus);

    if (onValidate) {
      setIsSubmitting(true);
      try {
        await onValidate(fieldName, newStatus);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <div className={`field-validator d-inline-flex align-items-center gap-1 ${className}`}>
      <button
        type="button"
        className={`btn ${sizeClasses[size]} ${
          localStatus === true ? 'btn-success' : 'btn-outline-success'
        }`}
        onClick={() => handleValidation(true)}
        disabled={disabled || isSubmitting}
        title={labels.true || 'Correct'}
        style={{ minWidth: size === 'sm' ? '32px' : '40px' }}
      >
        <i className="fas fa-check" />
      </button>
      <button
        type="button"
        className={`btn ${sizeClasses[size]} ${
          localStatus === false ? 'btn-danger' : 'btn-outline-danger'
        }`}
        onClick={() => handleValidation(false)}
        disabled={disabled || isSubmitting}
        title={labels.false || 'Wrong'}
        style={{ minWidth: size === 'sm' ? '32px' : '40px' }}
      >
        <i className="fas fa-times" />
      </button>
      {isSubmitting && (
        <span className="spinner-border spinner-border-sm text-primary" role="status">
          <span className="visually-hidden">Saving...</span>
        </span>
      )}
    </div>
  );
};

export default FieldValidator;
